"use client";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect } from "react";
import { ClickAwayListener, Box } from "@mui/material";
import { Info } from "@nxt-costco-com/forge-icons";
import { StyledButton, TooltipContent } from "./styles.js";
import { Arrow } from "../../Surfaces/Popover/styles.js";
import { handleTabKeyDown } from "../../Surfaces/Popover/util.js";
import { getDataTestId } from "../../Util/index.js";
const COMPONENT_NAME = "Tooltip";
const Tooltip = ({
  content,
  position = "bottom",
  hasArrow = false,
  buttonContent = /* @__PURE__ */ jsx(Info, {}),
  buttonStyles,
  buttonAriaLabel,
  buttonType = "button",
  isOpenByDefault = false,
  tooltipId,
  uniqueId,
  sx,
  offset,
  componentType = "p"
}) => {
  const buttonRef = useRef(null);
  const tooltipContentRef = useRef();
  const [arrowRef, setArrowRef] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const closeTooltip = useCallback(() => {
    setIsOpen(false);
  }, []);
  const handleFocus = useCallback(
    (event) => {
      var _a;
      if (event.key === "Enter" || event.key === " " || event.key === "Shift") {
        return;
      }
      if (event.key === "Tab") {
        openTooltip();
      }
      if (event.key === "Escape") {
        closeTooltip();
        (_a = buttonRef.current) == null ? void 0 : _a.focus();
      }
    },
    [closeTooltip]
  );
  useEffect(() => {
    setIsOpen(isOpenByDefault);
  }, [isOpenByDefault]);
  useEffect(() => {
    if (isOpen && tooltipContentRef && tooltipContentRef.current) {
      document.addEventListener("keydown", handleFocus);
    }
    return () => {
      document.removeEventListener("keydown", handleFocus);
    };
  }, [isOpen, handleFocus]);
  if (buttonAriaLabel === void 0) {
    console.error(
      `${COMPONENT_NAME}-error: buttonAriaLabel prop is required`
    );
    return null;
  }
  if (tooltipId === void 0) {
    console.error(`${COMPONENT_NAME}-error: tooltipId prop is required`);
    return null;
  }
  const handleClickAway = (event) => {
    var _a;
    if (buttonRef.current && ((_a = buttonRef == null ? void 0 : buttonRef.current) == null ? void 0 : _a.contains(event.target))) {
      return;
    }
    closeTooltip();
  };
  const handleTooltipContentRef = (node) => {
    var _a;
    tooltipContentRef.current = node;
    (_a = tooltipContentRef.current) == null ? void 0 : _a.focus();
  };
  const handlekeyDown = (event) => {
    const { key } = event;
    if (key === " " || key === "Enter") {
      event.preventDefault();
      toggleTooltip();
    } else if (key === "Escape") {
      event.preventDefault();
      closeTooltip();
    }
  };
  const handleOnKeyDown = (event) => {
    handleTabKeyDown(event, tooltipContentRef, buttonRef, () => {
      var _a;
      closeTooltip();
      (_a = buttonRef.current) == null ? void 0 : _a.focus();
    });
  };
  const openTooltip = () => {
    setIsOpen(true);
  };
  const toggleTooltip = () => {
    setIsOpen(!isOpen);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      StyledButton,
      {
        "aria-expanded": isOpen,
        "aria-label": typeof buttonContent === "string" ? "" : buttonAriaLabel,
        id: tooltipId,
        onClick: toggleTooltip,
        onKeyDown: handlekeyDown,
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        style: buttonStyles,
        ref: buttonRef,
        type: buttonType,
        children: buttonContent
      }
    ),
    /* @__PURE__ */ jsx(ClickAwayListener, { onClickAway: handleClickAway, children: /* @__PURE__ */ jsxs(
      TooltipContent,
      {
        "aria-describedby": tooltipId,
        sx,
        anchorEl: buttonRef.current,
        disablePortal: true,
        onKeyDown: handleOnKeyDown,
        open: isOpen,
        placement: position,
        role: "tooltip",
        tabIndex: -1,
        ref: (node) => handleTooltipContentRef(node),
        modifiers: [
          {
            name: "arrow",
            enabled: hasArrow,
            options: {
              element: arrowRef
            }
          },
          {
            name: "offset",
            enabled: offset !== void 0,
            options: {
              offset
            }
          }
        ],
        children: [
          hasArrow ? /* @__PURE__ */ jsx(
            Arrow,
            {
              component: "span",
              className: "arrow",
              ref: setArrowRef
            }
          ) : null,
          /* @__PURE__ */ jsx(
            Box,
            {
              component: componentType,
              sx: {
                border: "none",
                padding: 0,
                margin: 0,
                ":focus-visible": { outline: "none" }
              },
              children: content
            }
          )
        ]
      }
    ) })
  ] });
};
Tooltip.displayName = COMPONENT_NAME;
export {
  Tooltip
};
